import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { IconButton } from './Button';

/**
 * Modal and Drawer are built on the native <dialog> element: a real focus trap, inert background,
 * Escape handling and top-layer stacking for free. Content mounts only while open, so every open
 * starts from a clean state. The panel is glass-over-navy-blur in both themes.
 */

let openCount = 0;
function lockScroll(lock: boolean): void {
  openCount = Math.max(0, openCount + (lock ? 1 : -1));
  document.documentElement.style.overflow = openCount > 0 ? 'hidden' : '';
}

interface ShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Buttons pinned to the bottom. */
  footer?: ReactNode;
  /** When false, Escape and backdrop clicks do nothing (use while a save is in flight). */
  dismissible?: boolean;
  /** Visually hide the title bar text but keep it for screen readers. */
  hideTitle?: boolean;
  dialogClassName: string;
  panelClassName: string;
  /** Extra element in the header (badges, status). */
  headerAside?: ReactNode;
}

function DialogShell({ open, onClose, title, description, children, footer, dismissible = true, hideTitle, dialogClassName, panelClassName, headerAside }: ShellProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const latest = useRef({ onClose, dismissible });
  useEffect(() => {
    latest.current = { onClose, dismissible };
  });

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    const onCancel = (event: Event): void => {
      event.preventDefault();
      if (latest.current.dismissible) {
        latest.current.onClose();
      }
    };
    // A press on the backdrop targets the <dialog> itself; the panel fills the rest.
    const onPointerDown = (event: MouseEvent): void => {
      if (latest.current.dismissible && event.target === dialog) {
        latest.current.onClose();
      }
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('mousedown', onPointerDown);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !open) {
      return;
    }
    dialog.showModal();
    lockScroll(true);
    return () => {
      lockScroll(false);
      if (dialog.open) {
        dialog.close();
      }
    };
  }, [open]);

  return (
    <dialog ref={ref} aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} className={dialogClassName}>
      {open && (
        <div className={cn('flex max-h-[inherit] min-h-0 flex-col', panelClassName)}>
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div className="min-w-0">
              <h2 id={titleId} className={cn('font-display text-lg font-semibold text-ink', hideTitle && 'sr-only')}>
                {title}
              </h2>
              {description && (
                <p id={descriptionId} className="mt-0.5 text-sm text-ink-muted">
                  {description}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {headerAside}
              <IconButton label="Close" size="sm" disabled={!dismissible} onClick={onClose} icon={<X className="h-4 w-4" aria-hidden="true" />} />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 scroll-thin">{children}</div>
          {footer && <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line bg-sunken/50 px-6 py-3">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

const modalSizes = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' } as const;

export interface ModalProps extends Omit<ShellProps, 'dialogClassName' | 'panelClassName'> {
  size?: keyof typeof modalSizes;
}

export function Modal({ size = 'md', ...rest }: ModalProps) {
  return (
    <DialogShell
      {...rest}
      dialogClassName={cn('m-auto w-[calc(100%-1.5rem)] max-h-[calc(100dvh-2rem)] backdrop:bg-transparent', modalSizes[size])}
      panelClassName="glass-strong max-h-[calc(100dvh-2rem)] animate-scale-in overflow-hidden rounded-2xl shadow-pop ring-1 ring-line"
    />
  );
}

const drawerWidths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' } as const;

export interface DrawerProps extends Omit<ShellProps, 'dialogClassName' | 'panelClassName'> {
  side?: 'right' | 'left' | 'bottom';
  size?: keyof typeof drawerWidths;
}

/** Side sheet for detail panes and forms; on the `bottom` side it is a mobile bottom sheet. */
export function Drawer({ side = 'right', size = 'md', ...rest }: DrawerProps) {
  const position =
    side === 'bottom'
      ? 'mb-0 mt-auto w-full max-w-none'
      : cn('h-dvh max-h-dvh w-full', side === 'right' ? 'ml-auto mr-0' : 'ml-0 mr-auto', drawerWidths[size]);
  const panel =
    side === 'bottom'
      ? 'glass-strong max-h-[85dvh] animate-slide-up overflow-hidden rounded-t-2xl shadow-pop ring-1 ring-line pb-[env(safe-area-inset-bottom)]'
      : cn('glass-strong h-dvh animate-slide-in-right overflow-hidden shadow-pop ring-1 ring-line', side === 'left' && 'rounded-r-2xl', side === 'right' && 'rounded-l-2xl');
  return <DialogShell {...rest} dialogClassName={cn('backdrop:bg-transparent', position, side === 'bottom' && 'max-h-[85dvh]')} panelClassName={panel} />;
}

/** Sheet is an alias for a bottom Drawer (mobile "More" menu, filters). */
export function Sheet(props: Omit<DrawerProps, 'side'>) {
  return <Drawer side="bottom" {...props} />;
}
